import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import { type WbsDetails, wbsDetailsService } from '../services/wbsDetailsService';
import {
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { WbsDetailsDrawer } from './WbsDetailsDrawer';

type PanelMode = 'empty' | 'view' | 'edit' | 'add';

export const WbsDetailsManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const justSavedRef = useRef(false);
  const gridRef = useRef<AgGridReact>(null);

  const {
    data: rows = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['wbs-details'],
    queryFn: () => wbsDetailsService.getAll(),
    refetchOnMount: 'always',
  });

  const [panelMode, setPanelMode] = useState<PanelMode>('empty');
  const [selectedRow, setSelectedRow] = useState<WbsDetails | null>(null);
  const [editingRow, setEditingRow] = useState<WbsDetails | null>(null);

  const handleAdd = () => {
    setEditingRow(null);
    setPanelMode('add');
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this WBS detail?')) return;
    try {
      await wbsDetailsService.delete(id);
      queryClient.invalidateQueries({ queryKey: ['wbs-details'] });
      setSelectedRow(null);
      setPanelMode('empty');
    } catch (err) {
      toast.error(
        `Failed to delete WBS detail: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  const handleRowClicked = useCallback((event: RowClickedEvent<WbsDetails>) => {
    if (!event.data) return;
    setSelectedRow(event.data);
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleAddClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    setPanelMode('empty');
    setEditingRow(null);
  }, []);

  const handleEditClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingRow(null);
      return;
    }
    setPanelMode('view');
    setEditingRow(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedRow(null);
  }, []);

  const columnDefs: ColDef<WbsDetails>[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 100,
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'wbsNoLevel5',
        headerName: 'WBS No L5',
        width: 140,
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'wbsNameLevel5',
        headerName: 'WBS Name L5',
        flex: 1,
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'wbsNoLevel6',
        headerName: 'WBS No L6',
        width: 140,
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'wbsNameLevel6',
        headerName: 'WBS Name L6',
        flex: 1,
        valueFormatter: (p) => p.value || '-',
      },
    ],
    []
  );

  const gridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      onRowClicked: handleRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleRowClicked]
  );

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <div className="p-2">
      <style>{agGridThemeStyles}</style>
      <SettingsListDetailLayout
        detailOpen={detailOpen}
        detailVariant="default"
        detail={
          panelMode === 'add' ? (
            <WbsDetailsDrawer
              isOpen={true}
              onClose={handleAddClose}
              onSave={() => {}}
              onEntrySaved={() => {
                justSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['wbs-details'] });
                setPanelMode('empty');
                setSelectedRow(null);
                setEditingRow(null);
              }}
              row={null}
              isEdit={false}
            />
          ) : panelMode === 'edit' && editingRow ? (
            <WbsDetailsDrawer
              isOpen={true}
              onClose={handleEditClose}
              onSave={(row) => setSelectedRow(row)}
              onEntrySaved={() => {
                justSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['wbs-details'] });
                setPanelMode('view');
                setEditingRow(null);
              }}
              row={editingRow}
              isEdit
            />
          ) : panelMode === 'view' && selectedRow ? (
            <WbsDetailsDrawer
              key={selectedRow.id}
              isOpen={true}
              readOnly
              row={selectedRow}
              onClose={handleViewClose}
              onSave={() => {}}
              onEditRequest={() => {
                setEditingRow(selectedRow);
                setPanelMode('edit');
              }}
              onDeleteRequest={() => void handleDelete(selectedRow.id)}
            />
          ) : null
        }
        list={
          <>
            <div className="mb-4 flex shrink-0 items-center justify-between">
              <h2 className="m-0 text-2xl font-semibold text-primary">WBS Details</h2>
              <Button type="button" onClick={handleAdd}>
                <PlusIcon data-icon="inline-start" />
                Add WBS Detail
              </Button>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-4 p-8 text-text-secondary">
                <Spinner className="size-10" />
                <p>Loading WBS details...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center p-8 text-danger-600">
                <p>Error loading data: {error.message}</p>
                <Button type="button" className="mt-4" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            ) : rows.length === 0 ? (
              <p className="p-8 text-center text-text-secondary">
                No WBS details found. Add your first entry to get started.
              </p>
            ) : (
              <div
                className="ag-theme-material min-h-0 w-full flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  theme={agGridTheme}
                  rowData={rows}
                  columnDefs={columnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={gridOptions}
                  domLayout="normal"
                  rowNumbers
                  getRowId={(params) => String(params.data.id)}
                />
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
